/**
 * StrategyDetails Component
 *
 * Collapsible panel showing strategy configurations and exit plans.
 * Shows Conservative/Balanced/Aggressive in a compact table with backtest stats.
 */

import { useState } from 'react';
import './StrategyDetails.css';

// Strategy display configuration
const STRATEGY_ORDER = ['conservative', 'balanced', 'aggressive'];

const STRATEGY_COLORS = {
  conservative: '#4ade80',
  balanced: '#60a5fa',
  aggressive: '#f97316'
};

function formatLargeNumber(num) {
  if (num == null) return '0';
  if (Math.abs(num) >= 1e9) return (num / 1e9).toFixed(1) + 'B';
  if (Math.abs(num) >= 1e6) return (num / 1e6).toFixed(1) + 'M';
  if (Math.abs(num) >= 1e3) return (num / 1e3).toFixed(1) + 'K';
  return num.toFixed(1);
}

function StrategyDetails({ strategies = [], qualifyingStrategy }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedBacktest, setExpandedBacktest] = useState(null);

  // Build strategy map for easy lookup
  const strategyMap = {};
  strategies.forEach(s => {
    strategyMap[s.strategy_key] = s;
  });

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  const toggleBacktest = (key) => {
    setExpandedBacktest(expandedBacktest === key ? null : key);
  };

  return (
    <div className="strategy-details">
      {/* Collapsible Header */}
      <div className="strategy-details-header" onClick={toggleExpanded}>
        <h3>Strategy Details</h3>
        <span className="expand-icon">{isExpanded ? '▲' : '▼'}</span>
      </div>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="strategy-details-content">
          {/* Strategy Comparison Table */}
          <table className="strategy-table">
            <thead>
              <tr>
                <th></th>
                {STRATEGY_ORDER.map(key => (
                  <th
                    key={key}
                    style={{ color: STRATEGY_COLORS[key] }}
                    className={qualifyingStrategy === key ? 'active-strategy' : ''}
                  >
                    {key.charAt(0).toUpperCase() + key.slice(1)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Qualified Row */}
              <tr>
                <td className="row-label">Qualified?</td>
                {STRATEGY_ORDER.map(key => {
                  const strategy = strategyMap[key];
                  const isQualified = strategy?.entry_today;
                  return (
                    <td key={key} className={isQualified ? 'qualified' : 'not-qualified'}>
                      {isQualified ? '✓' : '✗'}
                    </td>
                  );
                })}
              </tr>

              {/* Min Dip Row */}
              <tr>
                <td className="row-label">Min Dip</td>
                {STRATEGY_ORDER.map(key => {
                  const strategy = strategyMap[key];
                  const minDip = strategy?.config?.min_dip_pct;
                  return (
                    <td key={key}>{minDip != null ? `${minDip}%` : '-'}</td>
                  );
                })}
              </tr>

              {/* ML Threshold Row */}
              <tr>
                <td className="row-label">ML Threshold</td>
                {STRATEGY_ORDER.map(key => {
                  const strategy = strategyMap[key];
                  const threshold = strategy?.config?.confidence_threshold;
                  return (
                    <td key={key}>{threshold != null ? `${Math.round(threshold * 100)}%` : '-'}</td>
                  );
                })}
              </tr>

              {/* Profit Target Row */}
              <tr>
                <td className="row-label">Profit Target</td>
                {STRATEGY_ORDER.map(key => {
                  const strategy = strategyMap[key];
                  const target = strategy?.config?.profit_target_pct;
                  return (
                    <td key={key}>{target != null ? `${target}%` : '-'}</td>
                  );
                })}
              </tr>

              {/* Max Hold Row */}
              <tr>
                <td className="row-label">Max Hold</td>
                {STRATEGY_ORDER.map(key => {
                  const strategy = strategyMap[key];
                  const maxHold = strategy?.config?.max_hold_days;
                  return (
                    <td key={key}>{maxHold != null ? `${maxHold} days` : '-'}</td>
                  );
                })}
              </tr>
            </tbody>
          </table>

          {/* Backtest Expandable Sections */}
          <div className="backtest-sections">
            {STRATEGY_ORDER.map(key => {
              const strategy = strategyMap[key];
              const backtest = strategy?.backtest || {};
              const isOpen = expandedBacktest === key;

              return (
                <div
                  key={key}
                  className={`backtest-section ${qualifyingStrategy === key ? 'active' : ''}`}
                >
                  <div
                    className="backtest-header"
                    onClick={() => toggleBacktest(key)}
                    style={{ borderLeftColor: STRATEGY_COLORS[key] }}
                  >
                    <span className="backtest-title">
                      {key.charAt(0).toUpperCase() + key.slice(1)} Backtest
                    </span>
                    <span className="backtest-summary">
                      {backtest.n_trades || 0} trades, {(backtest.win_rate || 0).toFixed(0)}% win
                    </span>
                    <span className="expand-icon">{isOpen ? '−' : '+'}</span>
                  </div>

                  {isOpen && (
                    <div className="backtest-details">
                      <div className="backtest-grid">
                        <div className="backtest-item">
                          <span className="backtest-value">{backtest.n_trades || 0}</span>
                          <span className="backtest-label">Total Trades</span>
                        </div>
                        <div className="backtest-item">
                          <span className="backtest-value">{(backtest.win_rate || 0).toFixed(1)}%</span>
                          <span className="backtest-label">Win Rate</span>
                        </div>
                        <div className="backtest-item">
                          <span className="backtest-value">{(backtest.trades_per_year || 0).toFixed(1)}</span>
                          <span className="backtest-label">Trades/Year</span>
                        </div>
                        <div className="backtest-item">
                          <span className="backtest-value">{(backtest.avg_return || 0).toFixed(2)}%</span>
                          <span className="backtest-label">Avg Return</span>
                        </div>
                      </div>
                      <div className="backtest-total">
                        Total Return: <span className="total-return">{formatLargeNumber(backtest.total_return)}%</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default StrategyDetails;
